import { ObjectType } from '~/static/enums/object-type'
import { VersionMigrator } from '~/utils/editor/versioning/migrators'
import { ObjectPropsV1, ObjectPropsV2, migrate as migrateObject } from '~/utils/editor/versioning/object'

export enum Versions {
  V1 = 1, // data has no field 'version'
  V2 = 2,
  V3 = 3,
  V4 = 4,
}

export const ActualVersionNumber = Versions.V4
export type ActualVersion = ImagePropsV4

export interface ImagePropsV1 extends ObjectPropsV1 {
  type?: ObjectType.Image

  imageName?: string
  width?: number
  height?: number
  cropWidth?: number
  cropHeight?: number
  offsetX?: number
  offsetY?: number
  borderRadius?: number
  border?: number
  borderColor?: string
  borderOpacity?: number
  borderDash?: boolean
  borderExists?: boolean
  shadow?: boolean
  shadowColor?: string
  shadowOpacity?: number
  shadowX?: number
  shadowY?: number
  shadowBlur?: number
  backRemoved?: boolean
  imageUrl?: string
  originalUrl?: string | null
  brightness?: number
  contrast?: number
  saturation?: number
  colorfulness?: number
  hue?: number
  opacity?: number
}

export interface ImagePropsV2 extends ObjectPropsV2 {
  type?: ObjectType.Image

  imageName?: string
  width?: number
  height?: number
  cropWidth?: number
  cropHeight?: number
  offsetX?: number
  offsetY?: number
  borderRadius?: number
  border?: number
  borderColor?: string
  borderOpacity?: number
  borderDash?: boolean
  borderExists?: boolean
  shadow?: boolean
  shadowColor?: string
  shadowOpacity?: number
  shadowX?: number
  shadowY?: number
  shadowBlur?: number
  backRemoved?: boolean
  imageUrl?: string
  originalUrl?: string
  brightness?: number
  contrast?: number
  saturation?: number
  colorfulness?: number
  hue?: number
  opacity?: number
}

export interface ImagePropsV3 extends ImagePropsV2 {
  hasOpacity?: boolean
  hasCorrection?: boolean
}

export interface ImagePropsV4 extends ImagePropsV3 {
  originalWidth?: number
  originalHeight?: number

  correctParamsRatio?: boolean
  isNeiro?: boolean
  isEnhanced?: boolean
}

export const migrate: VersionMigrator<ActualVersion> = (data: object) => {
  if ('version' in data && data.version === ActualVersionNumber) {
    return data as ActualVersion
  }

  const dataV1 = data as ImagePropsV1

  const dataV2 = {
    type: ObjectType.Image,
    ...migrateObject(dataV1),
    version: Versions.V2,
    title: dataV1.imageName,
    imageName: dataV1.imageName,
    width: dataV1.width,
    height: dataV1.height,
    cropWidth: dataV1.cropWidth,
    cropHeight: dataV1.cropHeight,
    offsetX: dataV1.offsetX,
    offsetY: dataV1.offsetY,
    borderRadius: dataV1.borderRadius,
    border: dataV1.border,
    borderColor: dataV1.borderColor,
    borderOpacity: dataV1.borderOpacity,
    borderDash: dataV1.borderDash,
    borderExists: dataV1.borderExists,
    shadow: dataV1.shadow,
    shadowColor: dataV1.shadowColor,
    shadowOpacity: dataV1.shadowOpacity,
    shadowX: dataV1.shadowX,
    shadowY: dataV1.shadowY,
    shadowBlur: dataV1.shadowBlur,
    backRemoved: dataV1.backRemoved,
    imageUrl: dataV1.imageUrl,
    originalUrl: dataV1.originalUrl ?? dataV1.imageUrl,
    brightness: dataV1.brightness,
    contrast: dataV1.contrast,
    saturation: dataV1.saturation,
    colorfulness: dataV1.colorfulness,
    hue: dataV1.hue,
    opacity: dataV1.opacity,
  } as ImagePropsV2

  return Object.assign(dataV2, {
    version: Versions.V4,
    hasOpacity: dataV2.opacity !== undefined && dataV2.opacity !== 1,
    hasCorrection: (dataV2.brightness !== undefined && dataV2.brightness !== 0) ||
      (dataV2.contrast !== undefined && dataV2.contrast !== 0) ||
      (dataV2.saturation !== undefined && dataV2.saturation !== 0) ||
      (dataV2.colorfulness !== undefined && dataV2.colorfulness !== 0) ||
      (dataV2.hue !== undefined && dataV2.hue !== 0),
  })
}
